import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import BackToTop from "@/components/feature/BackToTop";
import ScoopCard from "@/components/base/ScoopCard";
import { useSeo } from "@/hooks/useSeo";
import { scoops } from "@/mocks/scoops";
import { categories, type SortKey } from "@/mocks/categories";
import FilterBar from "./components/FilterBar";

export default function ScoopsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const initialCategory = searchParams.get("cat") ?? "全部";

  const [query, setQuery] = useState(initialQuery);
  const [category, setCategory] = useState(initialCategory);
  const [sort, setSort] = useState<SortKey>("latest");

  useEffect(() => {
    const next = new URLSearchParams();
    if (query.trim()) next.set("q", query.trim());
    if (category && category !== "全部") next.set("cat", category);
    setSearchParams(next, { replace: true });
  }, [query, category, setSearchParams]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const list = scoops.filter((scoop) => {
      const matchCategory = category === "全部" || scoop.category === category;
      const matchKeyword =
        !keyword ||
        scoop.title.toLowerCase().includes(keyword) ||
        scoop.summary.toLowerCase().includes(keyword) ||
        scoop.code.toLowerCase().includes(keyword) ||
        scoop.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });

    const sorted = [...list];
    if (sort === "heat") sorted.sort((a, b) => b.heat - a.heat);
    else if (sort === "credibility") sorted.sort((a, b) => b.credibility - a.credibility);
    else sorted.sort((a, b) => (a.updated < b.updated ? 1 : -1));
    return sorted;
  }, [query, category, sort]);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "爆料库｜拾真社求证档案库",
      url: "https://hdlyjgs.cn/scoops",
      inLanguage: "zh-CN",
      hasPart: scoops.map((scoop) => ({
        "@type": "Article",
        headline: scoop.title,
        url: `https://hdlyjgs.cn/scoop/${scoop.id}`,
        datePublished: scoop.date,
      })),
    }),
    [],
  );

  useSeo({
    title: "爆料库｜独家爆料求证档案列表 · 拾真社",
    description:
      "拾真社爆料库收录已完成求证的独家爆料档案，含实锤证据、原始来源、可信度评分与真假结论，支持按领域筛选与关键词检索。",
    keywords: "独家爆料列表,独家爆料档案,爆料求证,实锤证据,真假辨析",
    path: "/scoops",
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader activePath="/scoops" />
      <Breadcrumbs items={[{ label: "爆料库" }]} />
      <main className="mx-auto max-w-[1200px] px-4 py-10 md:px-8 md:py-14">
        <header className="mb-8 max-w-3xl">
          <p className="mb-2 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">
            <span className="inline-block h-px w-6 bg-primary-400" aria-hidden="true"></span>
            爆料库
          </p>
          <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            所有进入求证流程的档案
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            每份档案都保留了材料清单、来源数量与可信度评分。你可以按领域筛选，也可以直接搜索档案号，
            查看我们是如何一步步核到结论的。
          </p>
        </header>

        <FilterBar
          query={query}
          onQueryChange={setQuery}
          category={category}
          onCategoryChange={setCategory}
          sort={sort}
          onSortChange={setSort}
          resultCount={filtered.length}
        />

        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-[11px] text-foreground-500">
          <span className="whitespace-nowrap">
            <i className="ri-bookmark-line mr-1"></i>
            已收录 {scoops.length} 份档案
          </span>
          {categories.map((cat) => (
            <span key={cat.slug} className="whitespace-nowrap">
              {cat.name} {scoops.filter((s) => s.category === cat.name).length}
            </span>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((scoop) => (
              <ScoopCard key={scoop.id} scoop={scoop} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-md bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有匹配的档案</h3>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
              换个关键词试试，或者清除筛选条件看看全部档案。如果你关心的事件还没被求证，也可以把线索投给我们。
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setCategory("全部");
                }}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-foreground-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                清除筛选条件
              </button>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}